import { useCallback, useRef, useState } from 'react';
import { frameFlags, frameRobots, frameTokens } from '../api.js';
import type { FlagView, FrameRobot, LogEntry, TokenView } from '../api.js';
import {
  afterArchiveStep, afterFlagStep, afterMissileStep, applyStep, ARCHIVE_STEPS,
  expandMissileFlights, FLAG_STEPS, MISSILE_STEPS, playableSteps, shotFrom,
} from './useSteppedPlayback.js';
import type { Archive, DisplayRobot, Shot } from './useSteppedPlayback.js';

/**
 * Replaying a register the player has already watched, or missed.
 *
 * Everything needed is already stored: the events say what happened, and
 * `frame` says how the board stood going in. So this is entirely a
 * client-side matter — no rule is re-evaluated and the server is not
 * asked to do anything.
 *
 * Deliberately separate from `useSteppedPlayback`, which follows the
 * LIVE game and is driven by state arriving. This one is driven by the
 * player asking. Keeping them apart means a replay cannot be mistaken for
 * the current position, and the live playback cannot be disturbed by
 * someone reviewing an earlier register.
 *
 * The token layer comes from the frame, not from the live game. Reviewing
 * turn 3 at turn 12 used to draw turn 12's debris under turn 3's robots,
 * which is the same fault the whole-game replay was built to avoid. A
 * frame written before the layer was recorded still has none, and the
 * caller falls back to the current board for those.
 */

// Half a second between steps.
const STEP_MS = 500;

export interface ReplayState {
  /** Positions to draw while replaying, or null when not. */
  positions: Map<string, DisplayRobot> | null;
  /** The shot to draw at this beat, or null where nothing fired. */
  shot: Shot | null;
  /**
   * The board as it stood, or null when not replaying AND when the frame
   * predates the token layer. Both mean "use the live board".
   */
  tokens: TokenView[] | null;
  /**
   * What else the frame said about each robot: powered down, virtual,
   * eliminated, where its archive stood.
   *
   * Null on a frame written before these were recorded. The distinction
   * has to be made per FRAME rather than per field: an absent
   * `poweredDown` means "not powered down" in a frame that records them
   * and "not written down" in one that does not, and the two want
   * opposite answers. A frame carrying a token layer is one of the new
   * ones, which is what this is keyed on.
   */
  flags: Map<string, FrameRobot> | null;
  /**
   * Where the flags stood, walked along by the belts as the register
   * plays. Null when not replaying, and on a frame that does not say --
   * a course whose flags have never moved, or one written before they
   * were recorded. The course layout is what to draw for all three.
   *
   * Not the same thing as `flags` above, which is the robots.
   */
  courseFlags: FlagView[] | null;
  /**
   * Where each robot's archive stood, moved at step E as the register
   * plays. Null when not replaying.
   */
  archives: Map<string, Archive> | null;
  /** Which entry is being replayed, for labelling. */
  entry: LogEntry | null;
  /** The turn being replayed whole, or null when replaying one entry. */
  turn: number | null;
  replaying: boolean;
  start: (entry: LogEntry) => void;
  /**
   * Replays a whole turn's registers: every entry given that has a frame
   * and a register, in log order. Each entry starts from its own frame,
   * so the board is exactly as recorded at every step of the turn.
   * `stepMs` is the time between steps, half a second unless given.
   */
  startTurn: (entries: LogEntry[], stepMs?: number) => void;
  stop: () => void;
}

export function useReplay(): ReplayState {
  const [positions, setPositions] = useState<Map<string, DisplayRobot> | null>(null);
  const [tokens, setTokens] = useState<TokenView[] | null>(null);
  const [flags, setFlags] = useState<Map<string, FrameRobot> | null>(null);
  const [courseFlags, setCourseFlags] = useState<FlagView[] | null>(null);
  const [archives, setArchives] = useState<Map<string, Archive> | null>(null);
  const [entry, setEntry] = useState<LogEntry | null>(null);
  const [turn, setTurn] = useState<number | null>(null);
  // The shot being drawn this beat. One register can fire several, so it
  // is set by the step that fires and cleared by whatever follows.
  const [shot, setShot] = useState<Shot | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const stop = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPositions(null);
    setTokens(null);
    setFlags(null);
    setCourseFlags(null);
    setArchives(null);
    setEntry(null);
    setTurn(null);
    setShot(null);
  }, []);

  /**
   * Plays the first entry of `queue`, then the rest. Every entry here has
   * a frame. Between entries the board holds one beat; after the last it
   * holds two before handing back.
   */
  const play = useCallback((queue: LogEntry[], stepMs = STEP_MS) => {
    const [chosen, ...rest] = queue;
    const next = rest.length > 0 ? () => play(rest, stepMs) : stop;
    const hold = rest.length > 0 ? stepMs : stepMs * 2;
    if (!chosen.frame) return;

    const running = new Map<string, DisplayRobot>(
      frameRobots(chosen.frame).map((r) => [
        r.id,
        { x: r.x, y: r.y, facing: r.facing, damage: r.damage ?? 0 },
      ]),
    );
    let layer = frameTokens(chosen.frame);
    let flagLayer = frameFlags(chosen.frame);
    let marks = new Map<string, Archive>(frameRobots(chosen.frame).map((r) => [
      r.id, { archiveMarker: r.archiveMarker, archiveFlag: r.archiveFlag },
    ]));

    setEntry(chosen);
    setPositions(new Map(running));
    setTokens(layer);
    setCourseFlags(flagLayer);
    setArchives(marks);
    // Keyed on the layer: both arrived in the same change, so a frame
    // with tokens is one that recorded these too.
    setFlags(layer ? new Map(frameRobots(chosen.frame).map((r) => [r.id, r])) : null);

    // A missile moves a TOKEN, and its events carry the FIRER's id -- so
    // handing one to `applyStep` would teleport the shooter to wherever
    // its shell went. With a layer to fly it across they are walked a
    // square at a time; without one there is nothing to show, so they are
    // dropped rather than spending a beat each on nothing.
    const steps = layer
      ? expandMissileFlights(playableSteps(chosen.events))
      : playableSteps(chosen.events).filter((step) => !MISSILE_STEPS.has(step.type));

    if (steps.length === 0) {
      // Nothing moved. Hold the frame briefly so the player sees the
      // board as it was, rather than a flicker.
      timers.current.push(setTimeout(next, stepMs));
      return;
    }

    steps.forEach((step, i) => {
      timers.current.push(setTimeout(() => {
        // Every step, so a beat that fires nothing clears the last line.
        setShot(shotFrom(step, running));

        if (MISSILE_STEPS.has(step.type)) {
          if (layer) {
            layer = afterMissileStep(layer, step);
            setTokens(layer);
          }
        } else if (ARCHIVE_STEPS.has(step.type)) {
          // Step E, where the marker is taken -- and where the robot is
          // standing at this beat is where it is taken.
          marks = afterArchiveStep(marks, step, running.get(step.robotId));
          setArchives(marks);
        } else if (FLAG_STEPS.has(step.type)) {
          // A flag riding a belt, on the beat the belts run. Nothing to
          // move on a frame that did not record where the flags were: the
          // course's own are being drawn, and they do not move.
          flagLayer = afterFlagStep(flagLayer, step);
          if (flagLayer) setCourseFlags(flagLayer);
        } else {
          applyStep(running, step);
          setPositions(new Map(running));
        }

        // Hold the final position before moving on. TWO beats at the
        // end: the last step is the one worth looking at -- where a
        // robot ended up, what hit it -- and one beat is barely time to
        // register it before the live board cuts back in.
        if (i === steps.length - 1) {
          timers.current.push(setTimeout(next, hold));
        }
      }, stepMs * (i + 1)));
    });
  }, [stop]);

  const start = useCallback((chosen: LogEntry) => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    // Without a starting frame there is nothing to play from: the events
    // alone cannot say where a robot that did not move was standing.
    if (!chosen.frame) return;
    setTurn(null);
    play([chosen]);
  }, [play]);

  const startTurn = useCallback((given: LogEntry[], stepMs = STEP_MS) => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    const queue = given
      .filter((e) => e.frame && e.register !== null)
      .sort((a, b) => a.seq - b.seq);
    if (queue.length === 0) return;
    setTurn(queue[0].turn);
    play(queue, stepMs);
  }, [play]);

  return {
    positions, tokens, flags, courseFlags, archives, entry, turn, shot,
    replaying: positions !== null, start, startTurn, stop,
  };
}
